import React, { useEffect, useCallback, useState, useRef, useMemo } from "react";
import { Plus, RefreshCw } from "lucide-react";
import { useTheme } from "../../context/ThemeContext";
import { useToast } from "../../context/ToastContext";
import { useUser } from "../../context/UserContext";
import { ConfirmDialog } from '@ui';
import QueueStatusBar from '../common/QueueStatusBar';
import { useDelayedUnmount } from "../../hooks/useDelayedUnmount";

// === Hooks ===
import {
  useStoryboardState,
  useAssetOptions,
  useAIGeneration,
  useBatchOperations,
} from "../../hooks";

// === 子组件 ===
import StoryboardToolbar from "./StoryboardToolbar";
import ShotCardList from "./list-view/ShotCardList";
import ShotCardGrid from "./grid-view/ShotCardGrid";
import VirtualShotList from "./list-view/VirtualShotList";
import VirtualGrid from "./grid-view/VirtualGrid";
import ShotGalleryView from "./gallery-view/ShotGalleryView";
import VirtualTextList from "./list-view/VirtualTextList";
import SourceTextPanel from "./common/SourceTextPanel";
import DirectorAdvicePanel from "./common/DirectorAdvicePanel";
import GenerationPreflightPanel from "./common/GenerationPreflightPanel";
import ImagePreviewModal from "./common/ImagePreviewModal";
import RedrawModal from "./common/RedrawModal";
import GridSequenceWizard from "./common/GridSequenceWizard";
import SequenceVideoPanel from "./common/SequenceVideoPanel";
import SequentialImageModal from "./common/SequentialImageModal";
import BatchVideoPanel from "./common/BatchVideoPanel";
import MultiShotPanel from "./common/MultiShotPanel";
import AudioGeneratorModal from "../AudioGeneratorModal";

// === 工具函数 ===
import { fileToBase64, uploadFileToCos, uploadFileMultipart } from "../../utils/fileUtils";
import { authenticatedFetch } from "../../utils/api";
import { API_HOST } from "../../utils/constants";
import { syncGeneratedImageForRow } from "../../utils/storyboardImageSync";
import { getSequenceShotImage } from "../../utils/sequenceVideoCapabilities";
import { runTaskToCompletion } from "../../services/taskService";

const VIDEO_PANEL_PREFERENCE_VERSION = 1;
const getVideoPanelPreferenceKey = (userId) =>
  `storyboard.video-panel.v${VIDEO_PANEL_PREFERENCE_VERSION}.${encodeURIComponent(userId || "anonymous")}`;
const readVideoPanelPreference = (key) => {
  try {
    return localStorage.getItem(key) === "true";
  } catch {
    return false;
  }
};

const mergeDirectorContext = (existingContext, incomingContext) => {
  if (!incomingContext) return existingContext || null;
  if (!existingContext || typeof existingContext !== "object" || Array.isArray(existingContext)) {
    return incomingContext;
  }
  if (typeof incomingContext !== "object" || Array.isArray(incomingContext)) {
    return incomingContext;
  }
  return {
    ...existingContext,
    ...incomingContext,
    // 主镜拆分方法论是源数据；单镜导演建议只能补上下文，不能用空/简化结果覆盖它。
    methodology: existingContext.methodology ?? incomingContext.methodology,
  };
};

// 资源下拉组件
const AssetDropdown = ({ label, color, options, onSelect, emptyText, icon }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const dropdownRef = React.useRef(null);
  const { mounted, exiting } = useDelayedUnmount(isOpen, 150);

  React.useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 cursor-pointer"
        style={{
          backgroundColor: `${color}20`,
          color: color,
          border: `1px solid ${color}40`
        }}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <span>{label}</span>
      </button>
      
      {mounted && (
        <div className={`absolute top-full left-0 mt-2 w-[360px] max-h-[500px] overflow-y-auto rounded-xl shadow-2xl z-[100] py-2 ${exiting
          ? "animate-out fade-out slide-out-to-top-2 duration-150"
          : "animate-in fade-in slide-in-from-top-2 duration-150"}`}
          style={{ 
            backgroundColor: 'var(--color-bg-secondary)', 
            border: '1px solid var(--color-border-primary)'
          }}>
          {options.length > 0 ? (
            <>
              <div className="px-3 py-2 text-2xs uppercase tracking-wider font-bold"
                style={{ color: 'var(--color-text-muted)' }}>
                点击添加到{label === '角色' ? '选中的分镜' : '选中的分镜'}
              </div>
              {options.map(item => (
                <div 
                  key={item.id} 
                  onClick={() => {
                    onSelect(item);
                    setIsOpen(false);
                  }}
                  className="flex items-center gap-3 px-4 py-3 cursor-pointer transition-all duration-200 hover:bg-[var(--color-bg-tertiary)] border-b last:border-0"
                  style={{ 
                    borderColor: 'var(--color-border-primary)',
                    backgroundColor: 'transparent'
                  }}>
                  {item.imageUrl ? (
                    <img src={item.imageUrl} alt="" className="w-16 h-16 rounded-xl object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-16 h-16 rounded-xl flex items-center justify-center flex-shrink-0" 
                      style={{ backgroundColor: 'var(--color-bg-overlay)' }}>
                      <span className="text-3xl">{icon}</span>
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate" style={{ color: 'var(--color-text-primary)' }}>
                      {item.name}
                    </div>
                    {item.description && (
                      <div className="text-xs line-clamp-2 mt-0.5" style={{ color: 'var(--color-text-muted)' }}>
                        {item.description}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </>
          ) : (
            <div className="px-4 py-6 text-sm text-center" style={{ color: 'var(--color-text-muted)' }}>
              {emptyText}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * StoryboardTable - 分镜编辑器主组件
 * 
 * 重构后职责：
 * 1. 组合各种自定义 hooks 管理状态
 * 2. 渲染工具栏和不同视图
 * 3. 处理跨组件的协调逻辑
 */
const StoryboardTable = ({
  data,
  originalText,
  onDataChange,
  onSave,
  onOpenWorkstation,
  projectId,
  episodeId,
}) => {
  const { getTheme } = useTheme();
  const theme = getTheme();
  const toast = useToast();
  const { userId } = useUser() || {};
  const videoPreferenceKey = useMemo(() => getVideoPanelPreferenceKey(userId), [userId]);
  const [videoEnabled, setVideoEnabled] = useState(() => readVideoPanelPreference(videoPreferenceKey));
  const videoEnabledRef = useRef(videoEnabled);
  videoEnabledRef.current = videoEnabled;
  const videoPreferenceKeyRef = useRef(videoPreferenceKey);

  useEffect(() => {
    if (videoPreferenceKeyRef.current !== videoPreferenceKey) {
      videoPreferenceKeyRef.current = videoPreferenceKey;
      setVideoEnabled(readVideoPanelPreference(videoPreferenceKey));
      return;
    }
    try {
      localStorage.setItem(videoPreferenceKey, String(videoEnabled));
    } catch {
      // 隐私模式或存储被禁用时继续使用当前会话状态。
    }
  }, [videoEnabled, videoPreferenceKey]);

  // === 1. 核心状态管理 ===
  const {
    rows,
    setRows,
    viewMode,
    setViewMode,
    previewImage,
    setPreviewImage,
    copiedId,
    setCopiedIdWithTimeout,
    redrawModalOpen,
    closeRedrawModal,
    redrawTargetIndex,
    isSaving,
    setIsSaving,
    currentRatio,
    setCurrentRatio,
    showGridWizard,
    setShowGridWizard,
    openVideoSettingsIndex,
    setOpenVideoSettingsIndex,
    hoveredGridIndex,
    setHoveredGridIndex,
    selectedShotIndex,
    setSelectedShotIndex,
    audioModal,
    openAudioModal,
    closeAudioModal,
    currentStyle,
    updateStyle,
    isGlobalLocked,
    handleChange,
    handleInsertRow,
    handleDeleteRow,
    clearAutoSaveTimer,
    setPollInterval,
    clearPollInterval,
    clearAllPollIntervals,
  } = useStoryboardState({ initialData: data, onDataChange, projectId });
  const rowsRef = useRef(rows);
  rowsRef.current = rows;

  // 多选状态
  const [selectedShots, setSelectedShots] = useState(new Set());
  const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);

  // 序列视频面板状态
  const [showSequenceVideoPanel, setShowSequenceVideoPanel] = useState(false);
  // 一键生视频面板状态
  const [showBatchVideoPanel, setShowBatchVideoPanel] = useState(false);
  // 多镜头自定义分镜面板状态: null 或 shot index
  const [multiShotIndex, setMultiShotIndex] = useState(null);

  // 出场动画：面板延迟卸载
  const seqVideoPanelAnim = useDelayedUnmount(showSequenceVideoPanel, 150);
  const batchVideoPanelAnim = useDelayedUnmount(showBatchVideoPanel, 150);
  const multiShotOpen = multiShotIndex != null && !!rows[multiShotIndex];
  const multiShotPanelAnim = useDelayedUnmount(multiShotOpen, 150);
  const lastMultiShotIndexRef = useRef(null);
  if (multiShotOpen) lastMultiShotIndexRef.current = multiShotIndex;
  // 批量扩展到画布状态
  const [isExpandingToCanvas, setIsExpandingToCanvas] = useState(false);
  // 组图生成弹窗状态
  const [showSequentialModal, setShowSequentialModal] = useState(false);
  // 原小说右侧阅读抽屉
  const [isSourcePanelOpen, setIsSourcePanelOpen] = useState(false);
  // 导演建议右侧抽屉
  const [isDirectorPanelOpen, setIsDirectorPanelOpen] = useState(false);
  const [directorAdviceIndex, setDirectorAdviceIndex] = useState(null);
  const [directorAdvice, setDirectorAdvice] = useState(null);
  const [isDirectorAdviceLoading, setIsDirectorAdviceLoading] = useState(false);
  const [directorAdviceError, setDirectorAdviceError] = useState("");
  // 生成前预检弹窗
  const [preflightRequest, setPreflightRequest] = useState(null);
  const [isPreflightSubmitting, setIsPreflightSubmitting] = useState(false);

  // 检测是否是首尾帧模式
  const isFirstLastFrameMode = useMemo(() => {
    return rows?.some(r =>
      r.first_frame_prompt || r.last_frame_prompt ||
      r.firstFramePrompt || r.lastFramePrompt
    ) || false;
  }, [rows]);

  // === 2. 资源管理 ===
  const {
    characterOptions,
    sceneOptions,
    propOptions,
    isRefreshingAssets,
    refreshAssets,
  } = useAssetOptions();

  // === 3. AI 生成 ===
  // ref 持有 remapPollKeys（在 useAIGeneration 之后赋值），供 save 回调使用
  const remapPollKeysRef = useRef(null);

  /**
   * 保存后合并后端返回的真实 ID，同时迁移 pollKey
   * 只 patch id 字段，保留所有前端瞬态状态（isGenerating 等）
   */
  const applyBackendIds = useCallback((savedData) => {
    if (!savedData || !Array.isArray(savedData)) return;
    setRows((prev) => {
      if (prev.length !== savedData.length) return prev;
      let changed = false;
      const idMap = [];
      const merged = prev.map((row, i) => {
        const backendId = savedData[i]?.id;
        if (backendId != null && row.id !== backendId) {
          changed = true;
          idMap.push({ oldId: row.id, newId: backendId });
          return { ...row, id: backendId };
        }
        return row;
      });
      if (changed && idMap.length > 0) {
        remapPollKeysRef.current?.(idMap);
      }
      return changed ? merged : prev;
    });
  }, [setRows]);

  // 使用 ref 稳定 performAutoSave 回调，同时保存后合并后端返回的真实 ID
  const performAutoSaveImplRef = useRef(null);
  performAutoSaveImplRef.current = (latestRows) => {
    setTimeout(async () => {
      if (onDataChange) onDataChange(latestRows);
      if (onSave) {
        const savedData = await onSave(latestRows, setIsSaving);
        applyBackendIds(savedData);
      }
    }, 0);
  };
  // 稳定引用：传给 useAIGeneration，永远不变
  const stablePerformAutoSave = useCallback((latestRows) => {
    performAutoSaveImplRef.current?.(latestRows);
  }, []);
  
  const {
    handleGenerateImage,
    handleGenerateImageBatch,
    handleGenerateVideo,
    handleAutoVideoPrompt,
    handleRedraw,
    buildRefList,
    stopVideoGeneration,
    stopImageGeneration,
    handleBatchGenerateNormalImages,
    handleBatchGenerateSequenceVideos,
    handleGenerateViduVideo,
    handleBatchSequentialImages,
    resumeVideoPolling,
    resumeImagePolling,
    suspendVideoPolling,
    remapPollKeys,
  } = useAIGeneration({
    setRows,
    performAutoSave: stablePerformAutoSave,
    videoEnabled,
    setPollInterval,
    clearPollInterval,
    clearAllPollIntervals,
    projectId,
    episodeId,
  });

  // 赋值 ref，供 applyBackendIds 在 save 回调中迁移 pollKey
  remapPollKeysRef.current = remapPollKeys;

  // === 4. 批量操作 ===
  const {
    batchAction,
    batchProgress,
    batchGenerateVideos,
    startBatchAction,
    cancelBatchAction,
    finishBatchAction,
  } = useBatchOperations();

  // === Effects ===

  // 加载初始数据，并恢复 polling 状态的视频和图片任务
  // 用 ref 记录上次 data 的 id 签名，避免编辑时 data 引用变化导致 rows 被重置（滚动跳回顶部）
  const lastDataSignatureRef = useRef(null);
  useEffect(() => {
    if (data) {
      // 用 id 列表作为签名，只在数据源真正变化时（如切换集数、重新拆分）才重置 rows
      const signature = data.map(r => r.id).join(',');
      if (signature === lastDataSignatureRef.current) return;
      lastDataSignatureRef.current = signature;

      // 预处理：把 polling 状态的 row 标记为 isGeneratingVideo/isGenerating
      const processedData = data.map(row => {
        const updates = {};
        if (videoEnabledRef.current && row.video_task_status === "polling" && row.video_task_id && row.id) {
          updates.isGeneratingVideo = true;
        }
        if (row.image_task_status === "polling" && row.image_task_id && row.id) {
          updates.isGenerating = true;
        }
        return Object.keys(updates).length > 0 ? { ...row, ...updates } : row;
      });
      setRows(processedData);
      // 用 setTimeout 0 确保 setRows 先完成，再启动轮询，避免竞态条件覆盖状态
      setTimeout(() => {
        if (videoEnabledRef.current) resumeVideoPolling(processedData);
        resumeImagePolling(processedData);
      }, 0);
    }
  }, [data, setRows, resumeVideoPolling, resumeImagePolling]);

  // 隐藏视频区只暂停浏览器侧视频工作；任务 ID 与后端任务完整保留。
  useEffect(() => {
    if (!videoEnabled) {
      setOpenVideoSettingsIndex(null);
      setShowSequenceVideoPanel(false);
      setShowBatchVideoPanel(false);
      setMultiShotIndex(null);
      setPreflightRequest((current) => current?.type === "video" ? null : current);
      suspendVideoPolling();
      setRows((prev) => {
        let changed = false;
        const next = prev.map((row) => {
          if (!row.isGeneratingVideo) return row;
          changed = true;
          return { ...row, isGeneratingVideo: false };
        });
        return changed ? next : prev;
      });
      return;
    }

    let changed = false;
    const pendingRows = rowsRef.current.map((row) => {
      if (row.video_task_status !== "polling" || !row.video_task_id || !row.id || row.isGeneratingVideo) {
        return row;
      }
      changed = true;
      return { ...row, isGeneratingVideo: true };
    });
    if (changed) setRows(pendingRows);
    const resumeTimer = setTimeout(() => resumeVideoPolling(pendingRows), 0);
    return () => clearTimeout(resumeTimer);
  }, [videoEnabled, resumeVideoPolling, setOpenVideoSettingsIndex, setRows, suspendVideoPolling]);

  useEffect(() => {
    if (!videoEnabled && batchAction === "generateVideo") finishBatchAction();
  }, [batchAction, finishBatchAction, videoEnabled]);

  // 加载资源
  useEffect(() => {
    if (projectId) refreshAssets(projectId);
  }, [projectId, refreshAssets]);

  // 组件卸载清理
  useEffect(() => {
    return () => {
      clearAutoSaveTimer();
      clearAllPollIntervals();
    };
  }, [clearAutoSaveTimer, clearAllPollIntervals]);

  // === Handlers ===

  // 删除分镜：统一走确认弹窗
  const handleDeleteRowWithConfirm = useCallback((index) => {
    showConfirmDialog(
      '删除分镜',
      `确定要删除第 ${index + 1} 个镜头吗？此操作不可撤销。`,
      () => handleDeleteRow(index),
      { variant: 'danger' }
    );
  }, [handleDeleteRow]);

  // 插入行后立即触发保存（用于截帧插入等需要即时持久化的场景）
  const handleInsertRowAndSave = useCallback((index, initialData) => {
    handleInsertRow(index, initialData);
    // handleInsertRow 是同步 setState，下一个微任务 rows 已更新
    // 用 setRows 拿到最新 rows 触发 performAutoSave
    setTimeout(() => {
      setRows((latest) => {
        performAutoSaveImplRef.current?.(latest);
        return latest;
      });
    }, 0);
  }, [handleInsertRow, setRows]);

  // 延迟触发保存（用于 handleChange 后需要即时持久化的场景）
  const triggerSave = useCallback(() => {
    setTimeout(() => {
      setRows((latest) => {
        performAutoSaveImplRef.current?.(latest);
        return latest;
      });
    }, 0);
  }, [setRows]);

  const persistDirectorAdvice = useCallback((targetIndex, advice) => {
    if (!advice) return;
    setRows((prev) => {
      if (!Array.isArray(prev) || !prev[targetIndex]) return prev;
      const next = [...prev];
      const directorContext = mergeDirectorContext(
        next[targetIndex].director_context,
        advice.director_context,
      );
      next[targetIndex] = {
        ...next[targetIndex],
        director_notes: advice.director_notes || [],
        director_image_prompt: advice.director_image_prompt || "",
        director_video_prompt: advice.director_video_prompt || "",
        director_seedance_prompt: advice.director_seedance_prompt || "",
        // 单镜导演建议只补充上下文，不能覆盖主镜拆分阶段写入的方法论审计数据。
        director_context: directorContext,
        director_suggested_refs: advice.director_suggested_refs || {},
        director_context_snapshot: advice.director_context_snapshot || {},
      };
      return next;
    });
  }, [setRows]);

  const handleOpenDirectorAdvice = useCallback(async (index) => {
    const currentShot = rows[index];
    if (!currentShot) return;
    setDirectorAdviceIndex(index);
    setDirectorAdvice(null);
    setDirectorAdviceError("");
    setIsSourcePanelOpen(false);
    setIsDirectorPanelOpen(true);
    setIsDirectorAdviceLoading(true);

    try {
      const { result } = await runTaskToCompletion("STORYBOARD_DIRECTOR_ADVICE", {
          projectId,
          episodeId,
          shotId: currentShot.id && Number(currentShot.id) > 0 ? currentShot.id : null,
          shotIndex: index,
          currentShot,
          previousShot: index > 0 ? rows[index - 1] : null,
          nextShot: index < rows.length - 1 ? rows[index + 1] : null,
        }, {
          projectId,
          episodeId,
          shotId: currentShot.id && Number(currentShot.id) > 0 ? currentShot.id : undefined,
          source: "storyboard_director_advice",
          timeoutMs: 15 * 60 * 1000
      });
      const advice = result?.result || result?.value || result;
      setDirectorAdvice(advice);
      persistDirectorAdvice(index, advice);
    } catch (e) {
      const message = e.message || "导演建议生成失败";
      setDirectorAdviceError(message);
      toast.error(message, 0);
    } finally {
      setIsDirectorAdviceLoading(false);
    }
  }, [rows, projectId, episodeId, persistDirectorAdvice, toast]);

  const applyDirectorPrompt = useCallback((field, value) => {
    if (directorAdviceIndex == null || !value) return;
    handleChange(directorAdviceIndex, field, value);
    toast.success(field === "image_prompt" ? "已应用生图提示词" : "已应用视频提示词");
  }, [directorAdviceIndex, handleChange, toast]);

  const applyDirectorSeedancePrompt = useCallback(() => {
    if (directorAdviceIndex == null || !directorAdvice?.director_seedance_prompt) return;
    handleChange(directorAdviceIndex, "video_prompt", directorAdvice.director_seedance_prompt);
    toast.success("已应用参考演戏提示词");
  }, [directorAdviceIndex, directorAdvice, handleChange, toast]);

  const handleSaveAll = useCallback(async () => {
    clearAutoSaveTimer();
    if (onSave) {
      const savedData = await onSave(rows, setIsSaving);
      applyBackendIds(savedData);
    }
  }, [rows, onSave, clearAutoSaveTimer, setIsSaving, applyBackendIds]);

  const handleCopyPrompt = useCallback((text, id) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedIdWithTimeout(id, 1500);
  }, [setCopiedIdWithTimeout]);

  const handleSketchUpload = useCallback(async (index, file) => {
    if (!file) return;
    try {
      // 先显示本地预览，再异步上传 COS
      const base64 = await fileToBase64(file);
      handleChange(index, "sketch_preview", base64);
      // 上传 COS 拿到 URL，替换 base64
      const url = await uploadFileToCos(file);
      handleChange(index, "sketch_preview", url);
      handleChange(index, "sketch_base64", url);
    } catch {
      toast.error("草图上传失败");
    }
  }, [handleChange, toast]);

  const handleLocalImageUpload = useCallback(async (index, file) => {
    if (!file) return;
    try {
      // 先显示本地预览
      const base64 = await fileToBase64(file);
      setRows((prev) => {
        const n = [...prev];
        n[index] = {
          ...n[index],
          generated_image: base64,
          last_frame_image: null,
          lastFrameImage: null,
        };
        return n;
      });
      // 上传 COS 拿到 URL，替换 base64
      const url = await uploadFileToCos(file);
      setRows((prev) => {
        const n = [...prev];
        const row = n[index];
        if (!row) return prev;
        n[index] = {
          ...syncGeneratedImageForRow(row, url, {
            oldUrl: base64,
            source: "upload",
            model: "local-upload",
            clearLastFrame: true,
          }),
        };
        performAutoSaveImplRef.current?.(n);
        return n;
      });
      toast.success("图片上传成功");
    } catch {
      toast.error("图片上传失败");
    }
  }, [setRows, toast]);

  const handleLocalVideoUpload = useCallback(async (index, file) => {
    if (!file) return;
    try {
      // 先用本地 URL 预览
      const localUrl = URL.createObjectURL(file);
      setRows((prev) => {
        const n = [...prev];
        n[index] = { ...n[index], generated_video: localUrl };
        return n;
      });
      // 异步上传 COS，拿到持久 URL 后替换
      const cosUrl = await uploadFileMultipart(file);
      setRows((prev) => {
        const n = [...prev];
        n[index] = {
          ...n[index],
          generated_video: cosUrl,
          history_list: [cosUrl, ...(n[index].history_list || []).filter(h => h !== localUrl)].slice(0, 10),
        };
        return n;
      });
      URL.revokeObjectURL(localUrl);
      toast.success("视频上传成功");
    } catch (e) {
      toast.error("视频上传失败: " + e.message);
    }
  }, [setRows, toast]);

  const handleAudioSaveSuccess = useCallback((shotId, audioUrl) => {
    setRows((prev) =>
      prev.map((row) =>
        row.id === shotId ? { ...row, generated_audio: audioUrl } : row
      )
    );
    closeAudioModal();
    toast.success("音频已保存");
  }, [setRows, closeAudioModal, toast]);

  const handleRedrawSubmit = useCallback(async (prompt) => {
    if (redrawTargetIndex === null) return;
    await handleRedraw(redrawTargetIndex, prompt, rows, currentRatio);
    closeRedrawModal();
  }, [redrawTargetIndex, handleRedraw, rows, currentRatio, closeRedrawModal]);

  // ✅ 专门处理 Wizard 回填的逻辑，解决 ID 类型和闭包问题 (Fixed)
  const handleWizardUpdates = useCallback((updates) => {
    setRows((prevRows) => {
      // 创建更新映射：ID -> UpdateData
      // 确保 update.id 转为 string 与 row.id 类型一致
      const updateMap = new Map(updates.map((u) => [String(u.id), u]));
      
      const newRows = prevRows.map((row) => {
        const update = updateMap.get(String(row.id));
        if (update) {
          return {
            ...row,
            ...update, // 覆盖 sketch_base64, sketch_preview, status 等
            history_list: row.history_list, 
          };
        }
        return row;
      });
      
      performAutoSaveImplRef.current?.(newRows);
      return newRows;
    });
  }, [setRows]);

  // === 确认弹窗状态 ===
  const [confirmDialog, setConfirmDialog] = useState(null);

  const showConfirmDialog = (title, message, onConfirm, options = {}) => {
    setConfirmDialog({ title, message, onConfirm, ...options });
  };

  const handleConfirmDialogConfirm = (value) => {
    const callback = confirmDialog?.onConfirm;
    // 先关闭对话框
    setConfirmDialog(null);
    // 延迟执行回调，确保对话框完全关闭后再打开新对话框
    if (callback) {
      setTimeout(() => {
        callback(value);
      }, 100);
    }
  };

  // === Batch Handlers ===

  const executeBatchGenerateImages = useCallback(async () => {
    if (rows.length === 0) return toast.error("没有分镜可生成");

    // 检测是否是首尾帧模式（检查是否有 scene_number 字段）
    // if (rows.some(r => r.scene_number || r.sceneNumber)) {
      // 首尾帧模式：使用普通生图（每个分镜生成1张图片）
      showConfirmDialog(
        "一键生图",
        `确定要为当前 ${rows.length} 个分镜生成图片吗？`,
        async () => {
          startBatchAction("generateImage");
          try {
            const result = await handleBatchGenerateNormalImages(
              rows,
              currentStyle,
              currentRatio,
              buildRefList,
              characterOptions,
              sceneOptions,
              propOptions
            );

            if (result.failedCount > 0) {
              toast.warning(
                `批量生成完成：成功 ${result.successCount} 个，失败 ${result.failedCount} 个`
              );
            } else {
              toast.success(`批量生成完成，共 ${result.successCount} 个镜头`);
            }
          } catch (e) {
            toast.error(`批量生成失败: ${e.message}`);
          } finally {
            finishBatchAction();
          }
        }
      );
    // } 
    // else {
    //   // 普通模式（2x2宫格）
    //   showConfirmDialog(
    //     "一键生图",
    //     `确定要为当前 ${rows.length} 个分镜生成2x2宫格图片吗？（镜头间自动连戏）`,
    //     async () => {
    //       startBatchAction("generateImage");
    //       try {
    //         await handleBatchGenerateGrids(rows, currentStyle, currentRatio, projectId, episodeId);
    //         toast.success(`批量宫格生成完成，共 ${rows.length} 个镜头`);
    //       } catch (e) {
    //         toast.error(`批量生成失败: ${e.message}`);
    //       } finally {
    //         finishBatchAction();
    //       }
    //     }
    //   );
    // }
  }, [startBatchAction, finishBatchAction, rows, currentStyle, currentRatio,
      handleBatchGenerateNormalImages, buildRefList,
      characterOptions, sceneOptions, propOptions, toast]);

  // === 组图生成（wan2.7 enable_sequential） ===
  const executeBatchSequentialImages = useCallback(() => {
    if (rows.length < 2) return toast.error("至少需要2个分镜");
    setShowSequentialModal(true);
  }, [rows, toast]);

  const handleSequentialConfirm = useCallback(async (selectedIndices) => {
    setShowSequentialModal(false);
    const targetRows = selectedIndices.map(i => rows[i]).filter(Boolean);
    if (targetRows.length < 2) return toast.error("至少选择2个分镜");

    startBatchAction("sequentialImage");
    try {
      const result = await handleBatchSequentialImages(
        targetRows,
        selectedIndices,
        buildRefList,
        characterOptions,
        sceneOptions,
        propOptions,
        currentRatio,
        currentStyle
      );
      if (result.failedCount > 0) {
        toast.warning(`组图完成：成功 ${result.successCount} 张，未回填 ${result.failedCount} 张`);
      } else {
        toast.success(`组图生成完成，共 ${result.successCount} 张`);
      }
    } catch (e) {
      toast.error(`组图生成失败: ${e.message}`);
    } finally {
      finishBatchAction();
    }
  }, [startBatchAction, finishBatchAction, rows, handleBatchSequentialImages,
      buildRefList, characterOptions, sceneOptions, propOptions, currentRatio, currentStyle, toast]);

  const executeBatchGenerateVideos = useCallback(async () => {
    if (!videoEnabled) return;
    if (rows.length === 0) return toast.error("没有分镜可生成");

    // 检测是否是首尾帧模式（检查是否有 scene_number 字段）
    const isFirstLastFrameMode = rows.some(r => r.scene_number || r.sceneNumber);

    if (isFirstLastFrameMode) {
      // 首尾帧序列视频模式：使用相邻分镜的图片生成视频
      const rowsWithImage = rows.filter((r) => getSequenceShotImage(r));
      if (rowsWithImage.length < 2) {
        return toast.error("至少需要2个分镜有图片才能生成首尾帧视频");
      }

      showConfirmDialog(
        "一键首尾帧视频",
        `确定要生成 ${rowsWithImage.length - 1} 个首尾帧序列视频吗？（使用相邻分镜的图片）`,
        async () => {
          startBatchAction("generateVideo");
          try {
            await handleBatchGenerateSequenceVideos(rows, currentRatio);
            toast.success(`批量序列视频生成完成！`);
          } catch (e) {
            toast.error(`批量生成失败: ${e.message}`);
          } finally {
            finishBatchAction();
          }
        }
      );
    } else {
      // 普通模式 - 打开批量视频面板
      const rowsWithImage = rows.filter((r) => r.generated_image);
      if (rowsWithImage.length === 0) {
        return toast.error("请先一键生图，所有分镜都没有渲染图");
      }

      setShowBatchVideoPanel(true);
    }
  }, [startBatchAction, finishBatchAction, rows, currentRatio, handleBatchGenerateSequenceVideos, toast, videoEnabled]);

  // 一键下载所有视频
  const executeBatchDownloadVideos = useCallback(async () => {
    if (!videoEnabled) return;
    if (rows.length === 0) return toast.error("没有分镜可下载");

    // 过滤出有视频的分镜
    const rowsWithVideo = rows.filter(r => r.generated_video);
    if (rowsWithVideo.length === 0) {
      return toast.error("没有可下载的视频");
    }

    showConfirmDialog(
      "一键下载视频",
      `确定要下载 ${rowsWithVideo.length} 个分镜视频吗？`,
      async () => {
        toast.info(`开始下载 ${rowsWithVideo.length} 个视频...`);

        let successCount = 0;
        let failCount = 0;

        for (let i = 0; i < rowsWithVideo.length; i++) {
          const row = rowsWithVideo[i];
          const shotNumber = row.shot_number || row.shotNumber || (i + 1);

          // 命名格式：项目ID-集ID-分镜号.mp4
          const filename = `项目${projectId || 'unknown'}-集${episodeId || 'unknown'}-分镜${shotNumber}.mp4`;

          try {
            // 下载视频
            const response = await fetch(row.generated_video);
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);

            // 创建下载链接
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);

            // 释放 URL 对象
            setTimeout(() => URL.revokeObjectURL(url), 100);

            successCount++;

            // 添加延迟，避免浏览器阻止多个下载
            if (i < rowsWithVideo.length - 1) {
              await new Promise(resolve => setTimeout(resolve, 500));
            }
          } catch (error) {
            console.error(`下载视频失败: ${filename}`, error);
            failCount++;
          }
        }

        if (failCount > 0) {
          toast.warning(`下载完成：成功 ${successCount} 个，失败 ${failCount} 个`);
        } else {
          toast.success(`成功下载 ${successCount} 个视频`);
        }
      }
    );
  }, [rows, projectId, episodeId, toast, videoEnabled]);

  // 处理分镜多选
  const toggleShotSelection = useCallback((index) => {
    setSelectedShots(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  }, []);

  const clearShotSelection = useCallback(() => {
    setSelectedShots(new Set());
    setIsMultiSelectMode(false);
  }, []);

  const selectAllShots = useCallback(() => {
    setSelectedShots(new Set(rows.map((_, i) => i)));
  }, [rows]);

  // 批量扩展到画布9宫格（异步任务模式）
  const handleBatchExpandToCanvas = useCallback(async () => {
    if (rows.length === 0) {
      toast.error("没有分镜数据");
      return;
    }

    setIsExpandingToCanvas(true);

    try {
      // 1. 提交异步任务
      const { taskId } = await authenticatedFetch(`${API_HOST}/api/task/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          taskType: 'BATCH_EXPAND_TO_CANVAS',
          payload: { episodeId, provider: 'OMINI' },
          metadata: {},
        })
      });

      // 2. 存 taskId 到 localStorage，画布页面会检测并显示蒙层
      localStorage.setItem('batchExpandTaskId', taskId);
      localStorage.setItem('batchExpandEpisodeId', String(episodeId));

      toast.success('任务已提交，正在跳转画布模式...');

      // 3. 短暂延迟让用户看到状态变化，再跳转
      setTimeout(() => {
        window.location.href = `/project/${projectId}/episode/${episodeId}/canvas`;
      }, 800);

    } catch (error) {
      console.error('批量扩展失败:', error);
      toast.error('批量扩展失败: ' + error.message);
      setIsExpandingToCanvas(false);
    }
  }, [rows, episodeId, projectId, toast]);

  // === View Props ===

  // Window resize hook for reactive grid column count
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const gridColumnCount =
    windowWidth >= 1536 ? 6 :
    windowWidth >= 1280 ? 5 :
    windowWidth >= 1024 ? 4 :
    windowWidth >= 768 ? 3 : 2;

  const executeGenerateImage = useCallback(
    (index) => handleGenerateImage(index, rows, currentStyle, currentRatio, buildRefList, characterOptions, sceneOptions, propOptions),
    [handleGenerateImage, rows, currentStyle, currentRatio, buildRefList, characterOptions, sceneOptions, propOptions]
  );

  const executeGenerateImageBatch = useCallback(
    (index) => handleGenerateImageBatch(index, rows, currentStyle, currentRatio, buildRefList, characterOptions, sceneOptions, propOptions),
    [handleGenerateImageBatch, rows, currentStyle, currentRatio, buildRefList, characterOptions, sceneOptions, propOptions]
  );

  const executeGenerateVideo = useCallback(
    (index, multiShotConfig) => handleGenerateVideo(
      index, rows, currentRatio, "image_to_video", undefined,
      characterOptions, sceneOptions, propOptions, multiShotConfig
    ),
    [handleGenerateVideo, rows, currentRatio, characterOptions, sceneOptions, propOptions]
  );

  const openPreflight = useCallback((type, index, options = {}) => {
    if (type === "video" && !videoEnabled) return;
    if (!rows[index]) return;
    setPreflightRequest({ type, index, ...options });
  }, [rows, videoEnabled]);

  // Stable wrapped callbacks to avoid recreating view props objects
  const wrappedHandleGenerateImage = useCallback(
    (index) => openPreflight("image", index),
    [openPreflight]
  );

  const wrappedHandleGenerateImageBatch = useCallback(
    (index) => openPreflight("imageBatch", index),
    [openPreflight]
  );

  const wrappedHandleGenerateVideo = useCallback(
    (index, multiShotConfig) => openPreflight("video", index, { multiShotConfig }),
    [openPreflight]
  );

  const confirmPreflight = useCallback(async () => {
    if (!preflightRequest) return;
    if (preflightRequest.type === "video" && !videoEnabled) {
      setPreflightRequest(null);
      return;
    }
    setIsPreflightSubmitting(true);
    try {
      if (preflightRequest.type === "image") {
        await executeGenerateImage(preflightRequest.index);
      } else if (preflightRequest.type === "imageBatch") {
        await executeGenerateImageBatch(preflightRequest.index);
      } else if (preflightRequest.type === "video") {
        await executeGenerateVideo(preflightRequest.index, preflightRequest.multiShotConfig);
      }
      setPreflightRequest(null);
    } finally {
      setIsPreflightSubmitting(false);
    }
  }, [preflightRequest, executeGenerateImage, executeGenerateImageBatch, executeGenerateVideo, videoEnabled]);

  const wrappedHandleAutoVideoPrompt = useCallback(
    (index) => handleAutoVideoPrompt(index, rows, currentStyle),
    [handleAutoVideoPrompt, rows, currentStyle]
  );

  const onShowGridWizard = useCallback(() => setShowGridWizard(true), [setShowGridWizard]);

  const listViewProps = useMemo(() => ({
    rows,
    setRows,
    currentRatio,
    characterOptions,
    sceneOptions,
    propOptions,
    copiedId,
    openVideoSettingsIndex,
    setOpenVideoSettingsIndex,
    handleChange,
    handleDeleteRow: handleDeleteRowWithConfirm,
    handleInsertRow,
    handleInsertRowAndSave,
    handleCopyPrompt,
    handleSketchUpload,
    handleLocalImageUpload,
    handleLocalVideoUpload,
    handleGenerateImage: wrappedHandleGenerateImage,
    handleGenerateImageBatch: wrappedHandleGenerateImageBatch,
    handleGenerateVideo: wrappedHandleGenerateVideo,
    stopVideoGeneration,
    stopImageGeneration,
    handleAutoVideoPrompt: wrappedHandleAutoVideoPrompt,
    onOpenWorkstation,
    triggerSave,
    isGlobalLocked,
    onOpenAudio: openAudioModal,
    onOpenDirectorAdvice: handleOpenDirectorAdvice,
    onShowGridWizard,
    projectId,
    episodeId,
    selectedShots,
    toggleShotSelection,
    isMultiSelectMode,
    setIsMultiSelectMode,
    onOpenMultiShot: setMultiShotIndex,
    isFirstLastFrameMode,
    videoEnabled,
  }), [
    rows, currentRatio, characterOptions, sceneOptions, propOptions, copiedId,
    openVideoSettingsIndex, setOpenVideoSettingsIndex, handleChange, handleDeleteRowWithConfirm,
    handleInsertRow, handleInsertRowAndSave, handleCopyPrompt, handleSketchUpload, handleLocalImageUpload,
    handleLocalVideoUpload, wrappedHandleGenerateImage, wrappedHandleGenerateImageBatch, wrappedHandleGenerateVideo,
    stopVideoGeneration, stopImageGeneration, wrappedHandleAutoVideoPrompt,
    onOpenWorkstation, triggerSave, isGlobalLocked, openAudioModal, handleOpenDirectorAdvice, onShowGridWizard,
    projectId, episodeId, selectedShots, toggleShotSelection, isMultiSelectMode,
    setIsMultiSelectMode, setRows,
    isFirstLastFrameMode, videoEnabled,
  ]);

  const gridViewProps = useMemo(() => ({
    rows,
    currentRatio,
    hoveredGridIndex,
    setHoveredGridIndex,
    handleDeleteRow: handleDeleteRowWithConfirm,
    handleGenerateImage: wrappedHandleGenerateImage,
    stopImageGeneration,
    stopVideoGeneration,
    setPreviewImage,
    projectId,
    isGlobalLocked,
    videoEnabled,
  }), [
    rows, currentRatio, hoveredGridIndex, setHoveredGridIndex, handleDeleteRowWithConfirm,
    wrappedHandleGenerateImage, stopImageGeneration, stopVideoGeneration,
    setPreviewImage, projectId, isGlobalLocked, videoEnabled,
  ]);

  const galleryViewProps = useMemo(() => ({
    rows,
    selectedIndex: selectedShotIndex,
    setSelectedIndex: setSelectedShotIndex,
    characterOptions,
    sceneOptions,
    propOptions,
    handleChange,
    projectId,
    handleGenerateImage: wrappedHandleGenerateImage,
    handleGenerateImageBatch: wrappedHandleGenerateImageBatch,
    handleGenerateVideo: wrappedHandleGenerateVideo,
    stopVideoGeneration,
    stopImageGeneration,
    onOpenWorkstation,
    handleLocalImageUpload,
    currentRatio,
    handleInsertRow,
    handleDeleteRow: handleDeleteRowWithConfirm,
    handleSketchUpload,
    isGlobalLocked,
    onOpenAudio: openAudioModal,
    onOpenMultiShot: setMultiShotIndex,
    videoEnabled,
  }), [
    rows, selectedShotIndex, setSelectedShotIndex, characterOptions, sceneOptions,
    propOptions, handleChange, projectId, wrappedHandleGenerateImage,
    wrappedHandleGenerateImageBatch, wrappedHandleGenerateVideo, stopVideoGeneration,
    stopImageGeneration, onOpenWorkstation, handleLocalImageUpload, currentRatio,
    handleInsertRow, handleDeleteRowWithConfirm, handleSketchUpload, isGlobalLocked, openAudioModal,
    videoEnabled,
  ]);

  return (
    <div className="flex flex-col h-full w-full relative" style={{ backgroundColor: 'var(--color-bg-primary)' }}>
      {/* Toolbar */}
      <StoryboardToolbar
        viewMode={viewMode}
        setViewMode={setViewMode}
        currentRatio={currentRatio}
        setCurrentRatio={setCurrentRatio}
        currentStyle={currentStyle}
        setCurrentStyle={updateStyle}
        isSaving={isSaving}
        handleSaveAll={handleSaveAll}
        isGlobalLocked={isGlobalLocked}
        batchAction={batchAction}
        batchProgress={batchProgress}
        onBatchGenerateImages={executeBatchGenerateImages}
        onBatchGenerateVideos={executeBatchGenerateVideos}
        onBatchSequentialImages={executeBatchSequentialImages}
        onBatchGenerateSequenceVideos={() => videoEnabled && setShowSequenceVideoPanel(true)}
        onBatchDownloadVideos={executeBatchDownloadVideos}
        onBatchExpandToCanvas={handleBatchExpandToCanvas}
        isExpandingToCanvas={isExpandingToCanvas}
        onOpenSourceText={() => {
          setIsDirectorPanelOpen(false);
          setIsSourcePanelOpen(true);
        }}
        onCancelBatch={cancelBatchAction}
        rows={rows}
        isRefreshingAssets={isRefreshingAssets}
        fetchAssets={() => refreshAssets(projectId)}
        onShowGridWizard={() => setShowGridWizard(true)}
        isMultiSelectMode={isMultiSelectMode}
        setIsMultiSelectMode={setIsMultiSelectMode}
        selectedShots={selectedShots}
        selectAllShots={selectAllShots}
        clearShotSelection={clearShotSelection}
        videoEnabled={videoEnabled}
        onVideoEnabledChange={setVideoEnabled}
        projectId={projectId}
      />

      {/* Main Content */}
      <div className="flex-1 relative overflow-hidden w-full h-full">
        {viewMode === "list" && (
          <VirtualShotList
            rows={rows}
            listViewProps={listViewProps}
            onInsertAtTop={() => handleInsertRow(0)}
            onShowGridWizard={() => setShowGridWizard(true)}
            isGlobalLocked={isGlobalLocked}
            theme={theme}
          />
        )}

        {viewMode === "text" && (
          <VirtualTextList
            rows={rows}
            listViewProps={listViewProps}
            onInsertAtTop={() => handleInsertRow(0)}
            isGlobalLocked={isGlobalLocked}
          />
        )}

        {viewMode === "grid" && (
          <VirtualGrid
            rows={rows}
            gridViewProps={gridViewProps}
            columnCount={gridColumnCount}
          />
        )}

        {viewMode === "gallery" && (
          <div className="absolute inset-0 overflow-hidden"
            style={{ backgroundColor: 'var(--color-bg-primary)' }}>
            <ShotGalleryView {...galleryViewProps} />
          </div>
        )}

        <SourceTextPanel
          isOpen={isSourcePanelOpen}
          onClose={() => setIsSourcePanelOpen(false)}
          text={originalText}
          projectId={projectId}
          episodeId={episodeId}
        />

        <DirectorAdvicePanel
          isOpen={isDirectorPanelOpen}
          onClose={() => setIsDirectorPanelOpen(false)}
          loading={isDirectorAdviceLoading}
          error={directorAdviceError}
          advice={directorAdvice}
          shotIndex={directorAdviceIndex}
          onApplyImagePrompt={() => applyDirectorPrompt("image_prompt", directorAdvice?.director_image_prompt)}
          onApplyVideoPrompt={() => applyDirectorPrompt("video_prompt", directorAdvice?.director_video_prompt)}
          onApplySeedancePrompt={applyDirectorSeedancePrompt}
        />

        <GenerationPreflightPanel
          isOpen={!!preflightRequest && (videoEnabled || preflightRequest?.type !== "video")}
          type={preflightRequest?.type}
          shotIndex={preflightRequest?.index}
          rows={rows}
          currentRatio={currentRatio}
          sourceText={originalText}
          projectId={projectId}
          episodeId={episodeId}
          characterOptions={characterOptions}
          sceneOptions={sceneOptions}
          propOptions={propOptions}
          multiShotConfig={preflightRequest?.multiShotConfig}
          isSubmitting={isPreflightSubmitting}
          onClose={() => setPreflightRequest(null)}
          onConfirm={confirmPreflight}
        />
      </div>

      {/* Panels & Modals */}
      <ImagePreviewModal src={previewImage} onClose={() => setPreviewImage(null)} />
      
      <RedrawModal
        isOpen={redrawModalOpen}
        onClose={closeRedrawModal}
        onConfirm={handleRedrawSubmit}
        originalImage={redrawTargetIndex !== null ? rows[redrawTargetIndex]?.generated_image : null}
      />
      
      <GridSequenceWizard
        isOpen={showGridWizard}
        onClose={() => setShowGridWizard(false)}
        onUpdateShots={handleWizardUpdates}
        existingShots={rows}
        currentRatio={currentRatio}
        currentStyle={currentStyle}
        characterOptions={characterOptions}
        sceneOptions={sceneOptions}
        propOptions={propOptions}
      />

      <AudioGeneratorModal
        visible={audioModal.visible}
        initialText={audioModal.initialText}
        shotId={rows[audioModal.shotIndex]?.id}
        onClose={closeAudioModal}
        onSaveSuccess={handleAudioSaveSuccess}
      />

      {/* 组图生成弹窗 */}
      <SequentialImageModal
        isOpen={showSequentialModal}
        onClose={() => setShowSequentialModal(false)}
        onConfirm={handleSequentialConfirm}
        shots={rows}
      />

      {/* 序列视频面板 */}
      {videoEnabled && seqVideoPanelAnim.mounted && (
        <SequenceVideoPanel
          shots={rows}
          projectId={projectId}
          exiting={seqVideoPanelAnim.exiting}
          onGenerateSequence={async (config) => {
            try {
              await handleBatchGenerateSequenceVideos(
                rows,
                currentRatio,
                config.provider,
                config.model,
                config.shotDurations, // 传递每个分镜的时长映射
                config.resolution,
              );
              toast.success("序列视频生成完成！");
            } catch (e) {
              toast.error(`序列视频生成失败: ${e.message}`);
            }
          }}
          onClose={() => setShowSequenceVideoPanel(false)}
          isGenerating={batchAction === 'generateVideo'}
        />
      )}

      {/* 一键生视频面板 */}
      {videoEnabled && batchVideoPanelAnim.mounted && (
        <BatchVideoPanel
          shots={rows}
          exiting={batchVideoPanelAnim.exiting}
          onGenerate={async (config) => {
            setShowBatchVideoPanel(false);
            startBatchAction("generateVideo");

            try {
              const validIndices = rows
                .map((r, i) => (r.generated_image ? i : null))
                .filter((i) => i !== null);

              // 构建 viduConfig 对象（兼容 Vidu 和 Kling）
              const viduConfig = {
                resolution: config.resolution,
                audio: config.audio,
                audioType: config.audioType,
                bgm: config.bgm,
                offPeak: config.offPeak,
                movementAmplitude: config.movementAmplitude,
                voiceId: config.voiceId,
                seed: config.seed,
                promptExtend: config.promptExtend,
                watermark: config.watermark,
                // Kling 参数
                mode: config.mode,
                sound: config.sound,
                multiShot: config.multiShot,
                shotType: config.shotType,
                watermarkEnabled: config.watermarkEnabled,
              };

              await batchGenerateVideos(
                validIndices,
                (index) => {
                  // 从 shotDurations 获取该分镜的时长
                  const shotId = rows[index].id;
                  const duration = config.shotDurations[shotId];
                  return handleGenerateViduVideo(
                    index,
                    rows,
                    currentRatio,
                    characterOptions,
                    sceneOptions,
                    propOptions,
                    config.provider,
                    config.model,
                    { ...viduConfig, duration }
                  );
                }
              );

              toast.success(`批量视频生成完成，共 ${validIndices.length} 个镜头`);
            } catch (e) {
              toast.error(`批量生成失败: ${e.message}`);
            } finally {
              finishBatchAction();
            }
          }}
          onClose={() => setShowBatchVideoPanel(false)}
          isGenerating={batchAction === 'generateVideo'}
          onShotPromptChange={(shotId, value) => {
            console.log('[onShotPromptChange] shotId=', shotId, 'value=', value?.slice(0, 30));
            setRows((prev) => {
              const idx = prev.findIndex(r => r.id === shotId);
              console.log('[onShotPromptChange] findIndex=', idx, 'ids=', prev.map(r => r.id));
              if (idx === -1) return prev;
              const n = [...prev];
              n[idx] = { ...n[idx], video_prompt: value };
              return n;
            });
          }}
        />
      )}

      {/* 多镜头自定义分镜面板 */}
      {videoEnabled && multiShotPanelAnim.mounted && (() => {
        const shotIndex = multiShotOpen ? multiShotIndex : lastMultiShotIndexRef.current;
        const shot = rows[shotIndex];
        if (!shot) return null;
        // 从 Kling 时长字段读取，如果没有则从 time 字段读取
        const totalDuration = shot.video_kling_duration || parseInt((shot.time || '10s').replace('s', ''), 10) || 10;
        return (
          <MultiShotPanel
            currentShot={shot}
            totalDuration={totalDuration}
            characterOptions={characterOptions}
            sceneOptions={sceneOptions}
            propOptions={propOptions}
            isGenerating={shot.isGeneratingVideo}
            exiting={multiShotPanelAnim.exiting}
            onClose={() => setMultiShotIndex(null)}
            onGenerate={(config) => {
              setMultiShotIndex(null);
              wrappedHandleGenerateVideo(shotIndex, config);
            }}
          />
        );
      })()}

      {/* 确认弹窗 */}
      <ConfirmDialog
        isOpen={!!confirmDialog}
        onClose={() => setConfirmDialog(null)}
        onConfirm={handleConfirmDialogConfirm}
        title={confirmDialog?.title || '确认'}
        message={confirmDialog?.message || ''}
        confirmLabel="确定"
        variant={confirmDialog?.variant || "accent"}
        customButtons={confirmDialog?.customButtons}
      />
    </div>
  );
};

export default StoryboardTable;
